import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Check, Clock, AlertCircle, Info } from 'lucide-react'
import { emailActionApi, type EmailActionContext } from '@/services/api/emailAction'
import { getLessonRowStyle } from '@/utils/lessonRowColors'
import { sortLessonsByDateTime } from '@/utils/lessonSort'
import { LessonDecisionToggle, type LessonDecision } from '@/components/teacher-request/LessonDecisionToggle'
import { DayDecisionToggle, type DayDecision } from '@/components/email-action/DayDecisionToggle'
import { useAuthStore } from '@/store'
import { applyMagicSession, toInternalPath } from '@/utils/authRedirect'

type ViewState = 'loading' | 'error' | 'ready' | 'done'

const TERMINAL_RESULT_STATES = new Set(['done', 'too_late', 'already_done', 'closed'])

function resultIcon(state: string) {
  if (state === 'done') return <Check className="w-8 h-8 text-green-600" />
  if (state === 'too_late' || state === 'closed') return <Clock className="w-8 h-8 text-orange-500" />
  if (state === 'already_done') return <Info className="w-8 h-8 text-blue-500" />
  return <AlertCircle className="w-8 h-8 text-red-500" />
}

function resultTone(state: string) {
  if (state === 'done') return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
  if (state === 'too_late' || state === 'closed') return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
  if (state === 'already_done') return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
  return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
}

export function EmailActionPage() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const statusParam = params.get('status')
  const actionOverride = params.get('action') ?? undefined

  const [ctx, setCtx] = useState<EmailActionContext | null>(null)
  const [view, setView] = useState<ViewState>('loading')
  const [error, setError] = useState<string>('')
  const [result, setResult] = useState<{ state: string; message: string } | null>(null)
  const [loginToken, setLoginToken] = useState<string | null>(null)
  const [decision, setDecision] = useState<LessonDecision>(null)
  const [submitting, setSubmitting] = useState(false)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [loginNote, setLoginNote] = useState<string | null>(null)
  const navigate = useNavigate()
  const fetchUser = useAuthStore(s => s.fetchUser)

  const resolve = useCallback(async () => {
    if (!token) return
    try {
      const data = await emailActionApi.resolve(token, actionOverride)
      setCtx(data)
      if (data.login_token) setLoginToken(data.login_token)
      setDecision(data.preselect === 'open' ? null : data.preselect)
      if (data.state !== 'ready') {
        setResult({ state: data.state, message: data.message })
        setView('done')
      } else {
        setView('ready')
      }
    } catch (e: unknown) {
      const err = e as { detail?: string; message?: string }
      setError(err?.detail || err?.message || 'Link konnte nicht geladen werden')
      setView('error')
    }
  }, [token, actionOverride])

  useEffect(() => {
    if (token) resolve()
    else setView('error')
  }, [token, resolve])

  const handleConfirm = useCallback(async () => {
    if (!token) return
    setSubmitting(true)
    try {
      const choice = decision === 'decline' || decision === 'interested' ? decision : undefined
      const res = await emailActionApi.respond(token, actionOverride, undefined, choice)
      setResult({ state: res.state, message: res.message })
      if (res.login_token) setLoginToken(res.login_token)
      setView('done')
    } catch (e: unknown) {
      const err = e as { detail?: string }
      setResult({ state: 'error', message: err?.detail || 'Aktion konnte nicht ausgeführt werden' })
      setView('done')
    } finally {
      setSubmitting(false)
    }
  }, [token, actionOverride, decision])

  // Magic-link login: exchange the email token for a full session and continue
  // into the app as this teacher.
  const handleContinueToApp = async () => {
    if (!loginToken) return
    setIsLoggingIn(true)
    setLoginNote(null)
    try {
      const res = await emailActionApi.login(loginToken)
      if (res.access_token && res.refresh_token) {
        await applyMagicSession(res)
        await fetchUser()
        navigate(toInternalPath(res.redirect_url, '/teacher/incoming-requests'))
      } else {
        setLoginNote(res.message || 'Anmeldung nicht möglich. Bitte melden Sie sich an.')
      }
    } catch {
      setLoginNote('Anmeldung fehlgeschlagen. Bitte melden Sie sich an.')
    } finally {
      setIsLoggingIn(false)
    }
  }

  // Auto-confirm: for cancellation tokens the page executes immediately on load
  // (scanner-safe: scanners only GET and do not run JS). Fires once, only when ready.
  const autoConfirmFiredRef = useRef(false)
  useEffect(() => {
    if (autoConfirmFiredRef.current) return
    if (view === 'ready' && ctx?.auto_confirm) {
      autoConfirmFiredRef.current = true
      handleConfirm()
    }
  }, [view, ctx, handleConfirm])

  // Informational "Offen lassen" page (no token, no action).
  if (statusParam === 'open' && !token) {
    return (
      <Shell>
        <ResultCard state="already_done"
          message="Alles klar — Sie lassen diese Anfrage offen. Es wurde keine Aktion ausgeführt. Bei Bedarf können Sie später über die App oder eine erneute Anfrage antworten." />
      </Shell>
    )
  }

  if (view === 'loading') {
    return <Shell><div className="text-gray-500">Laden…</div></Shell>
  }

  if (view === 'error') {
    return <Shell><ResultCard state="error" message={error} /></Shell>
  }

  if (view === 'done' && result) {
    return (
      <Shell>
        <ResultCard state={result.state} message={result.message} />
        {loginToken && (
          <div className="mt-4 text-center">
            <button
              onClick={handleContinueToApp}
              disabled={isLoggingIn}
              className="w-full px-4 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 disabled:opacity-50 dark:bg-blue-600"
            >
              {isLoggingIn ? 'Anmelden…' : 'Weiter zur App (anmelden)'}
            </button>
            {loginNote && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{loginNote}</p>
            )}
          </div>
        )}
      </Shell>
    )
  }

  if (view === 'ready' && ctx!.action_kind === 'day_collect') {
    return (
      <Shell>
        <DayCollectCard
          ctx={ctx!}
          token={token!}
          actionOverride={actionOverride}
          submitting={submitting}
          setSubmitting={setSubmitting}
          onDone={(state, message, login) => {
            setResult({ state, message })
            if (login) setLoginToken(login)
            setView('done')
          }}
        />
      </Shell>
    )
  }

  // ready
  const preselect = ctx!.preselect
  const confirmDisabled = decision === null || submitting

  return (
    <Shell>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
            {ctx!.school_name}
          </div>
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-0.5">
            Vertretungsanfrage
          </h1>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Hallo {ctx!.teacher_name},
          </div>
        </div>

        <div className="p-5">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">{ctx!.message}</p>

          {ctx!.is_bulk && (
            <div className="mb-4 flex items-start gap-1.5 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-800 dark:text-blue-300">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Sammel-Antwort: Diese Aktion betrifft <strong>&nbsp;{ctx!.affected_request_count}&nbsp;Anfrage(n)</strong>
              &nbsp;({ctx!.lessons.length} Lektion(en)).
            </div>
          )}

          {ctx!.deadline && (
            <div className="mb-4 inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs font-semibold text-amber-700 dark:text-amber-300">
              <Clock className="w-3.5 h-3.5" />
              Antwortfrist: {new Date(ctx!.deadline).toLocaleString('de-DE', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              })}
            </div>
          )}

          {ctx!.lessons.length > 0 && (
            <div className="overflow-x-auto mb-5">
              <table className="w-full text-xs">
                <thead className="text-[10px] text-gray-500 dark:text-gray-400 uppercase border-b dark:border-gray-700">
                  <tr>
                    <th className="pb-1 text-left font-medium">Datum</th>
                    <th className="pb-1 text-left font-medium">Zeit</th>
                    <th className="pb-1 text-left font-medium">Fach</th>
                    <th className="pb-1 text-left font-medium">Klasse</th>
                    <th className="pb-1 text-left font-medium">Raum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {sortLessonsByDateTime(ctx!.lessons).map(l => {
                    const style = getLessonRowStyle(
                      l.status,
                      l.assigned_teacher_id ?? undefined,
                      ctx!.viewer_teacher_id ?? undefined,
                    )
                    return (
                      <tr key={l.lesson_request_id} className={style.row}>
                        <td className={`py-1.5 px-1 ${style.text}`}>{l.date || '-'}</td>
                        <td className={`py-1.5 px-1 font-semibold ${style.text}`}>{l.time || '-'}</td>
                        <td className={`py-1.5 px-1 font-medium ${style.text}`}>{l.subject || '-'}</td>
                        <td className={`py-1.5 px-1 ${style.text}`}>{l.class || '-'}</td>
                        <td className={`py-1.5 px-1 ${style.text}`}>{l.room || '-'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Ihre Antwort
              {preselect !== 'open' && (
                <>
                  {' '}(Mail-Link:{' '}
                  <strong className="text-gray-700 dark:text-gray-300">
                    {preselect === 'interested' ? 'Interessiert' : 'Ablehnen'}
                  </strong>)
                </>
              )}
              :
            </div>
            <LessonDecisionToggle
              decision={decision}
              onChoose={setDecision}
              disabled={submitting}
            />
          </div>

          <div className="flex gap-2 mt-5">
            <button
              onClick={handleConfirm}
              disabled={confirmDisabled}
              className="flex-1 px-4 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-600"
            >
              {submitting ? 'Wird gesendet…' : 'Bestätigen'}
            </button>
            <a
              href="?status=open"
              className="flex-1 px-4 py-2.5 text-center bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600"
            >
              Offen lassen
            </a>
          </div>
        </div>
      </div>
    </Shell>
  )
}

function rowTone(decision: DayDecision) {
  if (decision === 'accept') return { row: 'bg-green-100', text: 'text-green-800' }
  if (decision === 'decline') return { row: 'bg-red-100', text: 'text-red-800' }
  return { row: 'bg-orange-50', text: 'text-orange-800' }
}

function DayCollectCard({
  ctx, token, actionOverride, submitting, setSubmitting, onDone,
}: {
  ctx: EmailActionContext
  token: string
  actionOverride?: string
  submitting: boolean
  setSubmitting: (v: boolean) => void
  onDone: (state: string, message: string, login?: string | null) => void
}) {
  const days = ctx.days ?? []
  const [live, setLive] = useState(false)
  const [byDate, setByDate] = useState<Record<string, DayDecision>>(() => {
    const init: Record<string, DayDecision> = {}
    for (const d of days) init[d.date] = null
    if (ctx.entry_date && ctx.entry_intent) init[ctx.entry_date] = ctx.entry_intent
    return init
  })

  useEffect(() => {
    const id = window.setTimeout(() => setLive(true), 400)
    return () => window.clearTimeout(id)
  }, [])

  const openCount = days.filter(d => byDate[d.date] == null).length
  const acceptN = days.filter(d => byDate[d.date] === 'accept').length
  const declineN = days.filter(d => byDate[d.date] === 'decline').length
  const ready = days.length > 0 && (acceptN + declineN) > 0

  let confirmLabel = `Bestätigen · noch ${openCount} Tag(e) offen`
  if (ready && openCount === 0 && declineN === days.length) confirmLabel = `Alle ${days.length} Tage ablehnen`
  else if (ready && openCount === 0 && acceptN === days.length) confirmLabel = `Alle ${days.length} Tage buchen`
  else if (ready && openCount > 0) {
    const parts = []
    if (acceptN) parts.push(`${acceptN} buchen`)
    if (declineN) parts.push(`${declineN} ablehnen`)
    parts.push(`${openCount} offen`)
    confirmLabel = parts.join(', ')
  } else if (ready) confirmLabel = `${acceptN} Tag(e) buchen, ${declineN} ablehnen`

  const confirmClass = !ready
    ? 'bg-blue-500 hover:bg-blue-600'
    : declineN === days.length
      ? 'bg-red-500 hover:bg-red-600'
      : 'bg-green-500 hover:bg-green-600'

  const handleConfirm = async () => {
    if (!ready || submitting) return
    setSubmitting(true)
    try {
      const decisions = days
        .filter(d => byDate[d.date] === 'accept' || byDate[d.date] === 'decline')
        .map(d => ({
          date: d.date,
          decision: byDate[d.date] as 'accept' | 'decline',
        }))
      const res = await emailActionApi.respond(token, actionOverride, decisions)
      onDone(res.state, res.message, res.login_token)
    } catch (e: unknown) {
      const err = e as { detail?: string }
      onDone('error', err?.detail || 'Aktion konnte nicht ausgeführt werden')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-4 bg-blue-50 dark:bg-blue-950/40 border-b-2 border-blue-500">
        <div className="text-xs font-bold text-blue-800 dark:text-blue-300">{ctx.school_name}</div>
        <h1 className="text-lg font-bold text-blue-900 dark:text-blue-100 mt-0.5">Neue Vertretungsanfrage</h1>
        <div className="text-xs text-slate-600 dark:text-slate-400">
          <strong>Anfrage von</strong> {ctx.requester_name || '—'}
          {ctx.date_display ? ` · ${ctx.date_display}` : ''}
        </div>
      </div>

      <div className="p-4">
        <p className="text-sm text-slate-800 dark:text-slate-200 mb-3">Guten Tag {ctx.teacher_name},</p>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">{ctx.message}</p>

        {ctx.lessons.length > 0 && (
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-xs">
              <thead className="text-[10px] text-slate-500 uppercase border-b border-slate-200 dark:border-gray-700">
                <tr>
                  <th className="pb-1 text-left font-medium">Datum</th>
                  <th className="pb-1 text-left font-medium">Zeit</th>
                  <th className="pb-1 text-left font-medium">Fach</th>
                  <th className="pb-1 text-left font-medium">Klasse</th>
                  <th className="pb-1 text-left font-medium">Raum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white">
                {sortLessonsByDateTime(ctx.lessons).map(l => {
                  const tone = rowTone(byDate[l.date_iso || ''] ?? null)
                  return (
                    <tr key={l.lesson_request_id} className={tone.row}>
                      <td className={`py-1.5 px-1 ${tone.text}`}>{l.date || '-'}</td>
                      <td className={`py-1.5 px-1 font-semibold ${tone.text}`}>{l.time || '-'}</td>
                      <td className={`py-1.5 px-1 font-medium ${tone.text}`}>{l.subject || '-'}</td>
                      <td className={`py-1.5 px-1 ${tone.text}`}>{l.class || '-'}</td>
                      <td className={`py-1.5 px-1 ${tone.text}`}>{l.room || '-'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {ctx.deadline && (
          <div className="mb-4 inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs font-semibold text-amber-700 dark:text-amber-300">
            <Clock className="w-3.5 h-3.5" />
            Antwortfrist: {new Date(ctx.deadline).toLocaleString('de-DE', {
              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
            })}
          </div>
        )}

        <div className="text-center text-[11px] text-slate-500 mb-2">Pro Tag:</div>
        <div className="space-y-2">
          {days.map(d => {
            const flash = live && d.date === ctx.entry_date
            return (
              <div
                key={d.date}
                className={`flex items-center justify-between gap-2 flex-wrap px-3 py-2.5 rounded-[10px] border bg-white dark:bg-gray-800 ${
                  flash ? 'border-green-300 shadow-[0_0_0_4px_rgba(34,197,94,.18)]' : 'border-slate-200 dark:border-gray-700'
                }`}
              >
                <strong className="text-xs text-slate-600 dark:text-slate-300">{d.label}</strong>
                <div className="relative min-h-[34px] flex items-center justify-end">
                  <div
                    className={`flex gap-1.5 transition-all duration-500 ${live ? 'opacity-0 scale-90 pointer-events-none absolute' : 'opacity-100 scale-100'}`}
                    style={{ transitionTimingFunction: 'cubic-bezier(0.34,1.3,0.5,1)' }}
                  >
                    <span className="inline-block px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-green-500">Übernehmen</span>
                    <span className="inline-block px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-red-500">Ablehnen</span>
                  </div>
                  <div
                    className={`transition-all duration-500 ${live ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'}`}
                    style={{ transitionTimingFunction: 'cubic-bezier(0.34,1.3,0.5,1)' }}
                  >
                    <DayDecisionToggle
                      decision={byDate[d.date] ?? null}
                      onChoose={(val) => setByDate(prev => ({ ...prev, [d.date]: val }))}
                      disabled={submitting}
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className={`mt-5 transition-all duration-500 ${live ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!ready || submitting}
            className={`w-full px-4 py-3 text-white rounded-xl text-sm font-bold disabled:opacity-45 disabled:cursor-not-allowed ${confirmClass}`}
          >
            {submitting ? 'Wird gesendet…' : confirmLabel}
          </button>
          <p className="text-center text-[11px] text-slate-500 mt-2">
            {ready
              ? (openCount
                ? 'Offene Tage bleiben in der nächsten Welle anfragbar.'
                : 'Auswahl ist vollständig — noch nichts gebucht.')
              : 'Mindestens einen Tag übernehmen oder ablehnen. Offene Tage dürfen bleiben.'}
          </p>
        </div>
      </div>
    </div>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">{children}</div>
    </div>
  )
}

function ResultCard({ state, message }: { state: string; message: string }) {
  const tone = TERMINAL_RESULT_STATES.has(state) ? resultTone(state) : resultTone('error')
  return (
    <div className={`rounded-xl shadow-sm border p-8 text-center ${tone}`}>
      <div className="flex justify-center mb-3">{resultIcon(state)}</div>
      <p className="text-gray-800 dark:text-gray-100 text-base font-medium leading-relaxed">{message}</p>
    </div>
  )
}
